import { z } from "zod";

import { VibeLogColorSchema } from "./log-gateway-colors";
import { VIBE_RESERVED_LOG_APP_IDS } from "./log-reserved-app-ids";

/**
 * The wire contract of `vibe-log-gateway`, the per-tenant read path to Loki.
 *
 * ## Why a gateway exists at all
 *
 * Each tenant runs exactly ONE Loki, 1:1 with an organization via
 * `VibeTenantCluster`, and it runs `auth_enabled: false`. It therefore has no
 * `X-Scope-OrgID` tenancy and no authentication of its own. Multi-tenant safety
 * here is a ROUTING property, not a filter property.
 *
 * And the exposure is not read-only. Loki 3.x serves `/loki/api/v1/push`,
 * `/otlp` and `/loki/api/v1/delete` on the SAME port as its query API, so any
 * request that reaches port 3100 can INJECT and DELETE logs as well as read
 * them. Exposing raw Loki is not "a weaker version of a log viewer"; it is a
 * writable, deletable, unauthenticated log store.
 *
 * The gateway is the chosen shape because it is the ONLY one in which a TOTAL
 * auth bypass leaks a CONSTRAINED surface. It accepts a TYPED request and
 * composes the LogQL itself. It never forwards caller text as a query, it
 * exposes no other route, and it refuses every HTTP method Loki uses for
 * ingestion. Every other shape puts an unbounded query language, or a writable
 * ingestion port, behind the same single failure.
 *
 * ## What this file is, and what it is not
 *
 * This is the CONTRACT, shared by the daemon that serves it and the backend that
 * will call it. It is deliberately in `@nexus/types` rather than inside the
 * daemon: a caller that hand-rolls the request shape is a caller that can drift
 * from the limits below, and the limits are the security property.
 *
 * ## How it is reached
 *
 * On `logs.<tenantSlug>.<appDomain>` — see `composeVibeLogsHostName` in
 * `log-gateway-host.ts` — a host the tenant's own ALB and Traefik serve, guarded
 * by a ForwardAuth call into `vibe-edge-authz`'s platform decision path. Only a PLATFORM assertion
 * (`aud: "vibe-log-gateway"`, the org, a 60-second expiry) gets through; a user
 * session cookie is refused there, and a platform assertion is refused on every
 * app host. See `platform-assertion.schema.ts`.
 */

/**
 * Loopback only, and this constant is still the whole of the daemon's own network
 * boundary.
 *
 * The gateway performs NO authentication of its own — like `vibe-edge-authz`, it
 * is meaningful solely as something Traefik fronts. Bound to a routable
 * interface it would be an unauthenticated read path to every log the tenant
 * holds, on a node that also terminates public traffic, REACHABLE PAST the
 * ForwardAuth middleware rather than through it. Not configurable, for the same
 * reason `EDGE_AUTHZ_BIND_HOST` is not.
 */
export const VIBE_LOG_GATEWAY_BIND_HOST = "127.0.0.1";

/** Liveness + upstream reachability. Same spelling as the authorizer's. */
export const VIBE_LOG_GATEWAY_HEALTH_PATH = "/healthz";

/** The one route that answers a bounded page of logs. */
export const VIBE_LOG_GATEWAY_QUERY_PATH = "/v1/logs/query";

/** The one route that follows logs as they arrive, as Server-Sent Events. */
export const VIBE_LOG_GATEWAY_STREAM_PATH = "/v1/logs/stream";

/**
 * Every route the gateway serves, and there are no others.
 *
 * Exported so the refusal of everything else is assertable from the outside
 * rather than being a claim in a comment. A route added here without a handler
 * is a compile-time-visible gap; a handler added without a route here is
 * unreachable.
 */
export const VIBE_LOG_GATEWAY_ROUTES = [
  VIBE_LOG_GATEWAY_HEALTH_PATH,
  VIBE_LOG_GATEWAY_QUERY_PATH,
  VIBE_LOG_GATEWAY_STREAM_PATH
] as const;

/**
 * The HTTP methods the gateway will process AT ALL, checked before any routing.
 *
 * Loki ingests with POST (`/loki/api/v1/push`, `/otlp/v1/logs`) and deletes with
 * DELETE (`/loki/api/v1/delete`). POST is on this list because the query route
 * needs a body; DELETE, PUT and PATCH are refused outright, everywhere, ahead of
 * route matching — so a write method cannot reach a handler even if a future
 * route forgets to constrain its own method. The route table narrows it further:
 * each path accepts exactly one method.
 */
export const VIBE_LOG_GATEWAY_ALLOWED_METHODS = ["GET", "POST"] as const;

/** Hard ceiling on lines per page. The gateway clamps; it never trusts a caller. */
export const VIBE_LOG_GATEWAY_MAX_LIMIT = 5000;

/** Lines per page when the caller does not say. */
export const VIBE_LOG_GATEWAY_DEFAULT_LIMIT = 1000;

/** Hard ceiling on the width of a queryable window, in milliseconds. */
export const VIBE_LOG_GATEWAY_MAX_RANGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Hard ceiling on the length of the `contains` needle.
 *
 * Not a safety property on its own — the value is escaped either way — but an
 * unbounded literal is an unbounded query string, and Loki rejects those with a
 * 4xx that would surface to a user as "logs are broken".
 */
export const VIBE_LOG_GATEWAY_MAX_CONTAINS_LENGTH = 512;

/**
 * The Loki index label carrying the app id, and the ONE label the caller's
 * `appId` reaches.
 *
 * 🔴 UNDERSCORED HERE, DOTTED IN THE LOKI CONFIG, AND THE TWO SPELLINGS ARE NOT
 * INTERCHANGEABLE. `telemetry-loki-config.ts` promotes the OTLP resource attribute
 * `nexus.app.id` via `otlp_config.resource_attributes`, matching the attribute
 * name verbatim; the LABEL Loki then creates from it is `nexus_app_id`. A query
 * written against the dotted form matches nothing, and matches nothing SILENTLY —
 * it reads as an app that has never logged. The jobspec's own comment records
 * the mirror-image trap on its side.
 *
 * The promotion is explicit rather than inherited: that config sets
 * `ignore_defaults: true` and names `service.name`, `nexus.app.id` and
 * `nexus.deployment.color` as the ONLY index labels, so the label schema is a
 * property of that file rather than of the Loki release. Any attribute outside
 * that list is structured metadata — still queryable, just not indexable, and
 * therefore not usable in a `{}` selector.
 *
 * So this constant is one half of a cross-package contract. If the jobspec's
 * index-label list changes, this changes with it or the gateway returns empty
 * pages for every app.
 */
export const VIBE_LOG_GATEWAY_APP_ID_LABEL = "nexus_app_id";

/**
 * The deployment-slot label, matched as a LABEL FILTER rather than as a stream
 * selector matcher.
 *
 * It is an index label like the app id, so a `{}` matcher would also work. The
 * filter form is chosen because `color` is OPTIONAL: a label filter composes as
 * an appended stage, where an optional selector matcher would mean building the
 * selector's contents conditionally — the one part of the query where a branch
 * is worth avoiding. It also stays correct if the attribute ever leaves the
 * index-label list, since a Loki 3.x label filter reads structured metadata too.
 */
export const VIBE_LOG_GATEWAY_COLOR_LABEL = "nexus_deployment_color";

/**
 * The app a read is for. Composed into the stream selector by the gateway as an
 * EQUALITY matcher, so it selects exactly the stream whose `nexus_app_id` is this
 * value and no other.
 *
 * A platform stream's id is refused outright (`VIBE_RESERVED_LOG_APP_IDS`): the
 * agent's own lines and the canary are operator data, and this contract is the
 * one door every caller-composed read passes through — the backend parses it on
 * the way out, the gateway on the way in. A customer's app id is a `uuid` and can
 * never equal one of them; this refusal is what makes that a property of the read
 * path rather than of the id generator alone.
 */
const VibeLogAppIdSchema = z
  .string()
  .min(1)
  .max(128)
  .refine((appId) => !VIBE_RESERVED_LOG_APP_IDS.includes(appId), {
    message: "appId names a platform log stream, which no read may select"
  });

/**
 * A log query, as a caller states it.
 *
 * Every field is DATA. None of it is query syntax, and none of it names a label.
 * `contains` is the only free text, and it reaches Loki exclusively as an escaped
 * literal operand of `|=`. There is deliberately no field through which a caller
 * could supply a regular expression, a selector, a label name, or an aggregation.
 */
export const VibeLogQueryRequestSchema = z
  .object({
    /** The app whose logs to read. Composed into the stream selector by the gateway. */
    appId: VibeLogAppIdSchema,
    /** Window start, inclusive, as epoch milliseconds. */
    from: z.number().int().nonnegative(),
    /** Window end, exclusive, as epoch milliseconds. */
    to: z.number().int().nonnegative(),
    /** Restrict to one deployment slot. Absent means both. */
    color: VibeLogColorSchema.optional(),
    /** Case-sensitive LITERAL substring. Never a regular expression. */
    contains: z.string().trim().min(1).max(VIBE_LOG_GATEWAY_MAX_CONTAINS_LENGTH).optional(),
    /** Lines per page. Clamped to {@link VIBE_LOG_GATEWAY_MAX_LIMIT}. */
    limit: z
      .number()
      .int()
      .min(1)
      .max(VIBE_LOG_GATEWAY_MAX_LIMIT)
      .default(VIBE_LOG_GATEWAY_DEFAULT_LIMIT),
    /**
     * Page backwards from here — the `nextCursor` of the previous page, as a
     * nanosecond timestamp string. Nanoseconds because that is Loki's own entry
     * resolution: a millisecond cursor cannot separate two lines emitted inside
     * the same millisecond, so paging on one either repeats or drops them.
     */
    cursor: z
      .string()
      .regex(/^[0-9]+$/, "cursor must be a nanosecond epoch timestamp")
      .optional()
  })
  .strict()
  .refine((value) => value.from < value.to, {
    message: "`from` must be strictly before `to`"
  })
  .refine((value) => value.to - value.from <= VIBE_LOG_GATEWAY_MAX_RANGE_MS, {
    message: "the queried window must not exceed 7 days"
  });

export type VibeLogQueryRequest = z.infer<typeof VibeLogQueryRequestSchema>;

/**
 * A follow request. The same parameters, minus `to` and `cursor`.
 *
 * `to` is absent rather than optional because the end of a follow is always NOW,
 * moving. Accepting the field would let a caller ask to follow a window that has
 * already closed, which is a query wearing a stream's clothes.
 */
export const VibeLogStreamRequestSchema = z
  .object({
    appId: VibeLogAppIdSchema,
    /** Where the follow starts. Must be within 7 days of now, same ceiling as a query. */
    from: z.number().int().nonnegative(),
    color: VibeLogColorSchema.optional(),
    contains: z.string().trim().min(1).max(VIBE_LOG_GATEWAY_MAX_CONTAINS_LENGTH).optional(),
    limit: z
      .number()
      .int()
      .min(1)
      .max(VIBE_LOG_GATEWAY_MAX_LIMIT)
      .default(VIBE_LOG_GATEWAY_DEFAULT_LIMIT)
  })
  .strict();

export type VibeLogStreamRequest = z.infer<typeof VibeLogStreamRequestSchema>;

export * from "./log-gateway-colors";
export * from "./log-gateway-host";
export * from "./log-gateway-responses";
